import React from "react";
import get from "lodash/get";
import { connect } from "react-redux";
import moment from "moment-timezone";
import {
  Alert,
  Icon,
  Layout,
  Input,
  Select,
  Table,
  Badge,
  Button,
  Modal,
  Switch,
  message,
  Tooltip,
  Breadcrumb,
} from "antd";
import { Link } from "react-router-dom";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import SettingsSiteForm from "../../components/SettingsSiteForm/SettingsSiteForm";
import classes from "./SettingsSites.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getSettingsSitesRequest,
  deleteSiteRequest,
  activateSiteRequest,
  activateSiteRandomizationRequest,
  deleteSiteRandomizationRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getSiteCreatePermission,
  getSiteDeletePermission,
  getSiteUpdatePermission,
  getUserShortTimezone,
  getUserTimezone,
} from "../../reducers/user/selectors";

const routes = require("../../routes.json");

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;

class SettingsSites extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sitesList: [],
      errorMessage: "",
      responseMessage: "",
      siteEdit: null,
      nameSearch: null,
      sortedInfo: null,
      order: null,
    };
  }

  async componentDidMount() {
    this.getSitesList();
  }

  getSitesList = async () => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getSettingsSitesRequest({
        nameSearch: this.state.nameSearch,
        order_direction: this.state.order,
        order_column: get(this.state, 'sortedInfo.columnKey'),
      });
      this.setState({ sitesList: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  onNameSearch = async (value) => {
    this.setState({ nameSearch: value }, this.getSitesList);
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({
      isLoading: true,
      pageSize: pagination.pageSize,
      sortedInfo: sorter,
      order: get(sorter, 'order') === 'ascend' ? 'asc' : get(sorter, 'order') === 'descend' ? 'desc' : 'asc',
    }, () => this.getSitesList());
  };

  clearFilters = () => { this.setState({ nameSearch: null }, this.getSitesList); };

  onSwitchSiteActivation = (code, state) => async () => {
    try {
      if (state === "active") {
        await deleteSiteRequest(code);
      } else {
        await activateSiteRequest(code);
      }

      message.success("Saved");

      this.getSitesList();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error"));
      console.log(e);
    }
  };

  onSwitchSiteRandomization = (code, state) => async () => {
    try {
      if (state) {
        await deleteSiteRandomizationRequest(code);
      } else {
        await activateSiteRandomizationRequest(code);
      }

      message.success("Saved");

      this.getSitesList();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error"));
      console.log(e);
    }
  };

  onSiteEdit = (site) => () => {
    this.setState({ siteEdit: site });
  };

  onSiteEditHide = () => {
    this.setState({ siteEdit: null });
  };

  onSiteEditSubmit = () => {
    this.setState({ siteEdit: null }, this.getSitesList);
  };

  addSite = () => {
    this.setState({ siteEdit: {} });
  };

  render() {
    const {
      sitesList,
      errorMessage,
      responseMessage,
      isLoading,
      siteEdit,
      nameSearch,
      sortedInfo,
    } = this.state;
    const { siteCreatePermission } = this.props;

    const columns = [
      {
        dataIndex: "name",
        key: "name",
        title: "Site name",
        render: (val, record) => (
          <span>
          {val} &nbsp;{" "}
            <Badge
              count={record.state === "active" ? "Enabled" : "Disabled"}
              style={{
                backgroundColor: record.state === "active" ? "#52c41a" : "#777",
              }}
            />
        </span>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'name' && sortedInfo.order,
      },
      {
        dataIndex: "created",
        key: "created",
        title: "Created",
        render: (val, record) => (
          <span>
          {moment
            .utc(val)
            .tz(this.props.userTimezone || get(record, "time_zone") || "UTC")
            .format("YYYY-MM-DD HH:mm")}{" "}
            {this.props.userShortTimezone || get(record, "short_tz") || "UTC"}
        </span>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'created' && sortedInfo.order,
      },
      {
        dataIndex: "code",
        key: "code",
        title: "Code",
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'code' && sortedInfo.order,
      },
      {
        dataIndex: "location",
        key: "location",
        title: "Location",
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'location' && sortedInfo.order,
      },
      {
        dataIndex: "time_zone",
        key: "time_zone",
        title: "Time zone",
        render: (val, record) => (
          <span>
          {val} ({record.short_tz})
        </span>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'time_zone' && sortedInfo.order,
      },
      {
        dataIndex: "consent_workflow_type",
        key: "consent_workflow_type",
        title: "Consent Workflow Type",
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'consent_workflow_type' && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id",
        title: "Actions",
        render: (val, site) => (
          <div className={classes.actions}>
            <Tooltip placement="topLeft" title="Edit Site">
              <Icon
                type="edit"
                theme="filled"
                className={classes.actionIcon}
                onClick={this.onSiteEdit(site)}
              />
            </Tooltip>

            <div>
              <b></b>
              <br />
              <Tooltip placement="topLeft" title="Activate / Deactivate Site">
                <Switch
                  disabled={
                    (site.state === "active" &&
                      !this.props.siteDeletePermission) ||
                    (site.state === "inactive" &&
                      !this.props.siteUpdatePermission)
                  }
                  className={classes.activateBtn}
                  checked={site.state === "active"}
                  onChange={this.onSwitchSiteActivation(site.code, site.state)}
                />
              </Tooltip>
            </div>
          </div>
        ),
      },
      {
        dataIndex: "id",
        key: "randomization_permitted",
        title: "Randomization Active",
        render: (val, site) => (
          <div className={classes.actions} style={{ marginTop: "21px" }}>
            <Tooltip
              placement="topLeft"
              title="Activate / De-activate Site Randomization"
            >
              <Switch
                disabled={
                  (site.randomization_permitted &&
                    !this.props.siteDeletePermission) ||
                  (!site.randomization_permitted &&
                    !this.props.siteUpdatePermission) ||
                  site.state === "inactive"
                }
                className={classes.activateBtn}
                checked={site.randomization_permitted}
                onChange={this.onSwitchSiteRandomization(
                  site.code,
                  site.randomization_permitted
                )}
              />
            </Tooltip>
          </div>
        ),
      },
    ];

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <h3 className="headline-default">Site Management</h3>

            <Breadcrumb>
              <Breadcrumb.Item href="/admin/settings">
                Admin Settings
              </Breadcrumb.Item>
              <Breadcrumb.Item>Site Management</Breadcrumb.Item>
            </Breadcrumb>
            <br />

            <div className={classes.searchWrapper}>
              <label className={classes.searchWrapperSearch}>
                <b className={classes.searchWrapperLabel}>SEARCH</b>
                <Search
                  placeholder="By name"
                  enterButton="Search"
                  size="large"
                  onSearch={this.onNameSearch}
                  value={nameSearch}
                  onChange={(e) => { this.setState({ nameSearch: e.target.value }); }}
                  style={{ maxWidth: '500px'}}
                />
              </label>

              <span className={classes.clearButton} onClick={this.clearFilters}>Clear</span>
            </div>

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            <Table
              columns={columns}
              onChange={this.handleTableChange}
              dataSource={sitesList}
              loading={isLoading}
              rowKey="id"
              pagination={false}
            />

            <br />

            {siteCreatePermission && (
              <Button type="primary" onClick={this.addSite}>
                Add site
              </Button>
            )}

            <Modal
              visible={!!siteEdit}
              onCancel={this.onSiteEditHide}
              footer={null}
            >
              {siteEdit && (
                <SettingsSiteForm
                  {...siteEdit}
                  isNew={siteEdit && !siteEdit.id}
                  onSubmit={this.onSiteEditSubmit}
                  onCancel={this.onSiteEditHide}
                />
              )}
            </Modal>
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  siteCreatePermission: getSiteCreatePermission(state),
  siteDeletePermission: getSiteDeletePermission(state),
  siteUpdatePermission: getSiteUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(SettingsSites)
);



// WEBPACK FOOTER //
// ./src/containers/SettingsSites/SettingsSites.js